import {createAsyncThunk} from '@reduxjs/toolkit'

import {isApiError} from '@shared/helpers/api'
import {includes} from '@shared/helpers/collection'

import {OrderSheetPageSetType} from '@web/core/constants'
import {cardSelector, CardState} from '@web/slices/card/slice'
import {CommonState} from '@web/slices/common/slice'
import {DeliveryInfoState, addressesInfoSelector} from '@web/slices/deliveryInfo/slice'
import {
    orderSheetCouponSelector,
    OrderSheetCouponState,
    productItemDiscountInfoSelector,
} from '@web/slices/orderSheetCoupon/slice'
import {PaymentMethodState, payMethodAvailableSelector} from '@web/slices/paymentMethod/slice'
import {PurchaserState, membershipInfoSelector} from '@web/slices/purchaser/slice'

import api from './apis'
import {
    FetchIntegratedDiscountRequest,
    FetchIntegratedDiscountResponse,
    FetchIntegratedDiscountError,
} from './interfaces'
import {
    getGroupedCardPromotions,
    getMyOptimalDiscountChanged,
    getMyOptimalDiscountPromotion,
    getOptimizedCoupon,
} from './utils'

export type RootState = {
    common: CommonState
    deliveryInfo: DeliveryInfoState
    card: CardState
    orderSheetCoupon: OrderSheetCouponState
    purchaser: PurchaserState
    paymentMethod: PaymentMethodState
}

type CheckoutPayMethodType = NonNullable<
    FetchIntegratedDiscountRequest['params']['request']['availablePayMethodTypes']
>[number]

export type From = 'DELIVERY_RELOAD' | 'COUPON_RELOAD' | 'CARD_PROMOTION_RELOAD' | 'RESTORE'

type ThunkApi = {
    state: RootState
    rejectValue: FetchIntegratedDiscountError & {
        from?: From
    }
}

export const getIntegratedDiscount = createAsyncThunk<
    {
        from?: From
        data: FetchIntegratedDiscountResponse
        shouldSelectedUpdate: boolean
        // 나의할인가 조합이 변경되었는지 여부
        myOptimalDiscountDiff: {
            isGroupChanged: boolean
            isCouponChanged: boolean
            changed: boolean
        }
    },
    {
        // 요청 context
        from?: From
        pageSet: Extract<OrderSheetPageSetType, 'SELLER_CHECKOUT'>
        // selectedCoupon 업데이트 여부
        shouldSelectedUpdate?: boolean
    },
    ThunkApi
>('getIntegratedDiscount', async ({from, pageSet, shouldSelectedUpdate = false}, {getState, rejectWithValue}) => {
    try {
        const state = getState()
        const {deliveryAddress} = addressesInfoSelector(state)
        const {productCouponDiscountAmountByItemId: selectedDiscountAmountsByOrderSheetItemId} =
            productItemDiscountInfoSelector(state)
        const {subscribed} = membershipInfoSelector(state)
        const {myOptimizedInfo} = orderSheetCouponSelector(state)
        const {promotion} = cardSelector(state)
        const {availablePayMethodTypes: availableTypes} = payMethodAvailableSelector(state)

        const availablePayMethodTypes = availableTypes.filter(
            (payMethod) =>
                ![
                    'NONE',
                    'NAVERPAY_POINT',
                    'WELFARE_CARD',
                    'CULTURE_LAND',
                    'HAPPY_MONEY',
                    'MONEY_TOKEN',
                    'PAY_PAL',
                ].includes(payMethod),
        ) as CheckoutPayMethodType[]
        const isClearDiscountAmount = includes(from, ['COUPON_RELOAD', 'DELIVERY_RELOAD'])

        const result = await api.getIntegratedDiscount({
            pageSet,
            params: {
                orderSheetId: state.common.seqID,
                request: {
                    zipCode: deliveryAddress.zipCode,
                    buildingManagementNo: deliveryAddress.buildingManagementNo,
                    membershipSubscribed: subscribed,
                    availablePayMethodTypes,
                    /**
                     * 주문서 바닥에서 쿠폰조회실패 -> 재조회, 쿠폰모달에서 최대할인클릭 케이스에서는 selectedDiscountAmountsByOrderSheetItemId를 보내지 않는다.
                     * 해당케이스들은 현재 할인값과 상관없이 항상 최대할인으로 계산하기 때문
                     */
                    ...(!isClearDiscountAmount && {selectedDiscountAmountsByOrderSheetItemId}),
                },
            },
        })

        const curCoupon = result.couponFromMyOptimalDiscount
            ? getOptimizedCoupon(result.couponFromMyOptimalDiscount)
            : undefined
        const curGroups = result.cardPromotionV2
            ? getGroupedCardPromotions({
                  groups: result.cardPromotionV2.groupedCardPromotions,
                  failed: result.cardPromotionV2.failedCardPromotions,
              })
            : undefined
        const curGroup = curGroups ? getMyOptimalDiscountPromotion(curGroups) : undefined

        const myOptimalDiscountDiff = getMyOptimalDiscountChanged({
            prev: {
                coupon: myOptimizedInfo?.optimizedCoupon,
                group: promotion.myOptimalDiscountGroup,
            },
            curr: {
                coupon: curCoupon,
                group: curGroup,
            },
        })

        return {
            from,
            data: result,
            shouldSelectedUpdate,
            myOptimalDiscountDiff,
        }
    } catch (e) {
        if (isApiError(e)) {
            return rejectWithValue({
                ...e.response?.data?.result,
                from,
            })
        }
        throw e
    }
})
